import type { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useCrudEditContext,
  useDynamicFormsFetch,
  useModalConfirm,
  useStationRouteEdit,
} from "@ryde/redux";
import {
  ConfirmResult,
  errors,
  Log,
  parseSimpleResultError,
  TRANSLATION_KEY,
  TRANSLATION_PARAMS_KEY,
  warnAndToastSimpleResult,
  type IStationRouteDto,
} from "@ryde/shared";

/** Convert a system cache route to a customized ML path; replacing an existing one needs a second, explicit confirmation. */
export const useStationRouteConvertToCustomized = (): {
  onConvertToCustomizedClick: MouseEventHandler<HTMLButtonElement>;
} => {
  const { t } = useTranslation();
  const {
    data: { dto: stationRouteDto, fetchDto },
  } = useCrudEditContext<IStationRouteDto>();
  const { fetchTableRows } = useDynamicFormsFetch("stationRoute");
  const { convertToCustomized } = useStationRouteEdit();
  const { confirmDialogResult } = useModalConfirm();

  const onConvertToCustomizedClick: MouseEventHandler<HTMLButtonElement> =
    useEvent(async (e): Promise<void> => {
      e.preventDefault();

      const confirmationResult = await confirmDialogResult({
        title: t("stationRoute.CONVERT_TO_CUSTOMIZED_CONFIRM_TITLE"),
        message: t("stationRoute.CONVERT_TO_CUSTOMIZED_CONFIRM_MESSAGE"),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        return;
      }

      let convertResult = await convertToCustomized({ stationRouteDto });
      const convertError = parseSimpleResultError(
        errors.stationRoute.errorSavingRoute,
        convertResult,
      );

      if (
        !convertResult.success &&
        convertError[TRANSLATION_KEY] ===
          errors.stationRoute.customizedPathAlreadyExists
      ) {
        const replaceConfirmationResult = await confirmDialogResult({
          title: t("stationRoute.CONVERT_TO_CUSTOMIZED_REPLACE_CONFIRM_TITLE"),
          message: t(
            "stationRoute.CONVERT_TO_CUSTOMIZED_REPLACE_CONFIRM_MESSAGE",
            convertError[TRANSLATION_PARAMS_KEY],
          ),
          okButtonTitle: t("stationRoute.CONVERT_TO_CUSTOMIZED_REPLACE"),
        });

        if (replaceConfirmationResult !== ConfirmResult.OK) {
          return;
        }

        convertResult = await convertToCustomized({
          stationRouteDto,
          shouldReplaceExisting: true,
        });
      }

      if (!convertResult.success) {
        warnAndToastSimpleResult(convertResult, t);
        return;
      }

      if (stationRouteDto?.id) {
        await fetchDto?.(stationRouteDto.id);
      }

      fetchTableRows();

      Log.infoAndToast(t("stationRoute.CONVERT_TO_CUSTOMIZED_SUCCESS"));
    });

  return { onConvertToCustomizedClick };
};
